'use client';

import Link from 'next/link';
import type { ActivePackage } from '@/types/package';
import {
  BASIC_INCLUDED_FEATURES,
  COMPARISON_FEATURE_ROWS,
  formatInrPrice,
  getPackageBadge,
  getPackageSavings,
  getPaidPlanTheme,
} from '@/utils/membershipUtils';
import { Check, Crown, X } from 'lucide-react';

const FEATURES_COL_WIDTH = 220;
const PLAN_COL_MIN_WIDTH = 156;

function buildGridTemplateColumns(planColumnCount: number): string {
  return `${FEATURES_COL_WIDTH}px repeat(${planColumnCount}, minmax(${PLAN_COL_MIN_WIDTH}px, 1fr))`;
}

function buildTableMinWidth(planColumnCount: number): number {
  return FEATURES_COL_WIDTH + planColumnCount * PLAN_COL_MIN_WIDTH;
}

type DesktopMembershipComparisonProps = {
  packages: ActivePackage[];
  isAuthenticated: boolean;
  onSelectPackage: (packageId: string) => void;
};

function DesktopFeatureCell({
  included,
  value,
}: {
  included: boolean;
  value?: string | null;
}) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {included && value ? (
        <span className="text-xs font-semibold text-gray-900">({value})</span>
      ) : null}
      <div
        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${
          included ? 'bg-green-100' : 'bg-red-100'
        }`}
      >
        {included ? (
          <Check className="h-3.5 w-3.5 text-green-600" strokeWidth={2.5} />
        ) : (
          <X className="h-3.5 w-3.5 text-red-600" strokeWidth={2.5} />
        )}
      </div>
    </div>
  );
}

function CompactPaidPlanHeader({
  pkg,
  isAuthenticated,
  onSelectPackage,
}: {
  pkg: ActivePackage;
  isAuthenticated: boolean;
  onSelectPackage: (packageId: string) => void;
}) {
  const theme = getPaidPlanTheme();
  const savings = getPackageSavings(pkg);
  const badge = getPackageBadge(pkg);

  return (
    <div
      className={`relative flex h-full flex-col rounded-xl border bg-white p-4 text-center ${theme.borderClass}`}
    >
      {badge && (
        <div className="absolute -top-2.5 left-1/2 z-10 -translate-x-1/2">
          <div
            className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold leading-tight shadow-md ${badge.className}`}
          >
            {badge.label}
          </div>
        </div>
      )}

      <div
        className={`mx-auto mb-2.5 mt-1 flex h-11 w-11 items-center justify-center rounded-full ${theme.iconBgClass}`}
      >
        <Crown className={`h-5 w-5 ${theme.iconClass}`} strokeWidth={1.75} />
      </div>

      <h3 className={`mb-1 line-clamp-2 min-h-[2.5rem] text-sm font-bold leading-snug ${theme.titleClass}`}>
        {pkg.name}
      </h3>
      <div
        className={`mb-2 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${theme.pillClass}`}
      >
        {pkg.durationMonths} months
      </div>
      <div className={`text-2xl font-bold leading-none ${theme.priceClass}`}>
        {formatInrPrice(pkg.offerPrice)}
      </div>
      {savings ? (
        <>
          <div className="mt-1 text-xs text-gray-500 line-through">{formatInrPrice(pkg.price)}</div>
          <div className="mx-auto mt-2 inline-block rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold leading-tight text-green-700">
            Save {formatInrPrice(savings.amount)}
          </div>
        </>
      ) : (
        <div className="mt-1 text-[11px] text-gray-500">No hidden charges</div>
      )}

      <div className="mt-auto pt-3">
        {isAuthenticated ? (
          <button
            type="button"
            onClick={() => onSelectPackage(pkg.id)}
            className={`w-full rounded-lg px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors ${theme.buttonClass}`}
          >
            Buy Now
          </button>
        ) : (
          <Link
            href="/register"
            className={`inline-block w-full rounded-lg px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors ${theme.buttonClass}`}
          >
            Register Now in 2 minutes
          </Link>
        )}
      </div>
    </div>
  );
}

export default function DesktopMembershipComparison({
  packages,
  isAuthenticated,
  onSelectPackage,
}: DesktopMembershipComparisonProps) {
  const planColumnCount = 1 + packages.length;
  const gridTemplateColumns = buildGridTemplateColumns(planColumnCount);
  const tableMinWidth = buildTableMinWidth(planColumnCount);

  return (
    <div className="hidden w-full md:block">
      <div className="w-full overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:thin]">
        <div className="w-full" style={{ minWidth: `${tableMinWidth}px` }}>
          <div className="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
            <div className="border-b border-gray-200 bg-gradient-to-b from-gray-50 to-white px-4 py-5">
              <div className="grid items-stretch gap-3" style={{ gridTemplateColumns }}>
                <div className="sticky left-0 z-20 flex flex-col justify-end rounded-lg border border-gray-200 bg-white px-3 py-4 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.08)]">
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                    <Check className="h-5 w-5 text-gray-600" />
                  </div>
                  <h3 className="text-center text-sm font-bold text-gray-900">Features</h3>
                  <p className="mt-0.5 text-center text-xs text-gray-500">Compare plans</p>
                </div>

                <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-gray-50 px-3 py-4 text-center">
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-gray-200">
                    <Check className="h-5 w-5 text-gray-600" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-700">Free Plan</h3>
                  <div className="mt-1 inline-block self-center rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-700">
                    30 Days
                  </div>
                  <div className="mt-2 text-2xl font-bold text-gray-700">₹0</div>
                  <p className="mt-1 text-[11px] text-gray-500">Basic access</p>
                  <button
                    type="button"
                    disabled
                    className="mt-auto rounded-lg bg-gray-500 px-3 py-2 text-sm font-semibold text-white"
                  >
                    Current Plan
                  </button>
                </div>

                {packages.map((pkg) => (
                  <CompactPaidPlanHeader
                    key={pkg.id}
                    pkg={pkg}
                    isAuthenticated={isAuthenticated}
                    onSelectPackage={onSelectPackage}
                  />
                ))}
              </div>
            </div>

            <div className="px-4 py-2">
              {BASIC_INCLUDED_FEATURES.map((label) => (
                <div
                  key={label}
                  className="grid items-center gap-3 border-b border-gray-100 py-3 last:border-b-0"
                  style={{ gridTemplateColumns }}
                >
                  <div className="sticky left-0 z-10 border-r border-gray-100 bg-white py-1 pr-3 text-sm font-medium text-gray-900 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.08)]">
                    {label}
                  </div>
                  <div className="flex justify-center">
                    <DesktopFeatureCell included />
                  </div>
                  {packages.map((pkg) => (
                    <div key={`${pkg.id}-${label}`} className="flex justify-center">
                      <DesktopFeatureCell included />
                    </div>
                  ))}
                </div>
              ))}

              {COMPARISON_FEATURE_ROWS.map((row) => (
                <div
                  key={row.label}
                  className="grid items-center gap-3 border-b border-gray-100 py-3 last:border-b-0"
                  style={{ gridTemplateColumns }}
                >
                  <div className="sticky left-0 z-10 border-r border-gray-100 bg-white py-1 pr-3 text-sm font-medium text-gray-900 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.08)]">
                    {row.label}
                  </div>
                  <div className="flex justify-center">
                    <DesktopFeatureCell included={row.freeIncluded} />
                  </div>
                  {packages.map((pkg) => (
                    <div key={`${pkg.id}-${row.label}`} className="flex justify-center">
                      <DesktopFeatureCell
                        included={row.getIncluded(pkg)}
                        value={
                          isAuthenticated ? row.getDisplayValue?.(pkg) ?? null : null
                        }
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {packages.length >= 4 && (
        <p className="mt-3 text-center text-xs text-gray-500">
          Scroll horizontally to compare all membership plans
        </p>
      )}
    </div>
  );
}
